/**
 * The ordered Setup report (epic PI-SPEC-SW-E288, PKG-PM-H "Getting started").
 *
 * SpecTree Toolbox runs the steps; SpecTree's Setup page shows them as one
 * ordered list, in the order things must happen (03-evaluation.md section 8.6
 * Q10): 1 SpecTree, 2 Azure DevOps, 3 Claude Code (installs if missing, then
 * sign in), 4 Git (installs if missing), 5 Project code. A later step shows
 * "waits for N" (`waiting` with `waitsFor`) until the one before it passes.
 * Each row has at most one action that does the fix and a plain-words reason
 * on failure; never a command.
 *
 * GitHub-hosted repositories (epic PI-SPEC-SW-E318, ER-1683) add two actions
 * to step 5 for a project with a GitHub repository, appended to
 * {@link SETUP_STEP_ACTIONS} so older values keep their positions:
 * `install-github-cli` (installs the GitHub CLI for this person when it is
 * missing) and `sign-in-to-github` (one browser sign-in to GitHub, then Git
 * uses it for github.com only). No new step: the five steps stay as they are.
 *
 * The report crosses a version boundary (the app and the page release
 * separately), so the page parses it with {@link setupReportSchema}. It
 * replaces nothing: `checkEnvironment()` and `environmentReportSchema` stay
 * for older pages and apps. The bridge members that deliver it are optional
 * (`ToolboxBridge.app.getSetup`, ...); a page feature-detects them.
 *
 * Nothing here carries a credential. The Claude sign-in result carries a seat
 * label ("Premium seat"), never an email, token or password.
 */
import { z } from "zod";
// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------
/** The five Setup steps, in the order they must happen. */
export const SETUP_STEP_IDS = ["spectree", "azure-devops", "claude-code", "git", "project-code"];
/**
 * A step's state:
 * - `ok`: done;
 * - `running`: the app is doing it now (an install, a sign-in, a clone);
 *   `progress` may say how far;
 * - `todo`: the person has to act; `action` names the one button;
 * - `waiting`: blocked on an earlier step (`waitsFor`);
 * - `needs-role`: the person has no Toolbox role on the project yet (the
 *   page offers "Ask for a role", section 8.6 Q8);
 * - `failed`: the step failed; `detail` says why in plain words and `action`
 *   is usually `retry`.
 */
export const SETUP_STEP_STATUSES = ["ok", "running", "todo", "waiting", "needs-role", "failed"];
/** The one action a step's button runs through `runSetupStep`. */
export const SETUP_STEP_ACTIONS = [
    "install-claude-code",
    "sign-in-to-claude",
    "install-git",
    "get-project-code",
    "retry",
    "install-github-cli",
    "sign-in-to-github",
];
/** Longest plain-words reason on a step. */
export const SETUP_STEP_DETAIL_MAX = 300;
/** Longest button label on a step. */
export const SETUP_STEP_ACTION_LABEL_MAX = 60;
/** One row of the Setup list. Unknown keys from a newer app are stripped. */
export const setupStepSchema = z.object({
    id: z.enum(SETUP_STEP_IDS),
    status: z.enum(SETUP_STEP_STATUSES),
    /** Plain words, never a command: what is wrong or what is happening. */
    detail: z.string().max(SETUP_STEP_DETAIL_MAX).optional(),
    /** The one action the step's button runs. */
    action: z.enum(SETUP_STEP_ACTIONS).optional(),
    /** The button's label, for example "Sign in to Claude". */
    actionLabel: z.string().max(SETUP_STEP_ACTION_LABEL_MAX).optional(),
    /** How far a `running` step is, 0-100. */
    progress: z.number().min(0).max(100).optional(),
    /** For `waiting`: the earlier step this one waits for. */
    waitsFor: z.enum(SETUP_STEP_IDS).optional(),
});
/**
 * What `ToolboxBridge.app.getSetup()` and `runSetupStep()` resolve to: the
 * five steps, each exactly once, in {@link SETUP_STEP_IDS} order. A report
 * with a missing, duplicated or reordered step is refused.
 */
export const setupReportSchema = z
    .object({
    steps: z.array(setupStepSchema).length(SETUP_STEP_IDS.length),
})
    .refine((report) => report.steps.every((step, index) => step.id === SETUP_STEP_IDS[index]), {
    message: `steps must be ${SETUP_STEP_IDS.join(", ")}, each once, in that order`,
    path: ["steps"],
});
/** Setup changed: a step started, moved on, finished or failed. Carries the whole report. */
export const setupEventSchema = z.object({
    type: z.literal("setup-changed"),
    report: setupReportSchema,
});
/** True when every step is `ok`: the page unlocks Continue. */
export function isSetupComplete(report) {
    return report.steps.every((step) => step.status === "ok");
}
// ---------------------------------------------------------------------------
// Claude sign-in
// ---------------------------------------------------------------------------
/** Longest seat label, for example "Premium seat". */
export const CLAUDE_SEAT_LABEL_MAX = 60;
/**
 * What `ToolboxBridge.app.signInToClaude()` resolves to once the browser
 * sign-in finishes or fails. `seat` is the plan label to show ("Signed in,
 * Premium seat"); never an email, token or password. `reason` is one plain
 * sentence when `ok` is false. Unknown keys (an `email` from a careless app)
 * are stripped.
 */
export const claudeSignInResultSchema = z.object({
    ok: z.boolean(),
    seat: z.string().max(CLAUDE_SEAT_LABEL_MAX).optional(),
    reason: z.string().max(SETUP_STEP_DETAIL_MAX).optional(),
});
//# sourceMappingURL=setup.js.map